import type { GridRow, Market, PriceRun, PriceRunLine, PriceRuns, RefusedLine, ReviewedLine, StageBody } from "./api";
import { PricingError, reviewChange, reviewed } from "./api";
import { COPY, humanDate, money, personName, STORE_LABEL } from "./format";
import { OVERRIDE_COPY } from "./overrides";
import { RRP_COPY } from "./rrp";

/** Where a row's price lives: the Amazon marketplace, or ALL for a Shopify store and every ship country of it (one store price). */
export function placeOf(row: GridRow): string {
  return row.channel === "amazon" ? row.marketplace_id : "ALL";
}
/** Whose price a row carries: a ship-country row carries its store's. */
export function skuOf(row: GridRow): string {
  return row.grain === "ship_country" && row.parent_sku ? row.parent_sku : row.xero_sku;
}
export function lineMatches(line: PriceRunLine, row: GridRow): boolean {
  return line.xero_sku === skuOf(row) && line.marketplace_id === placeOf(row);
}
/** The stage body for a row: an Amazon row names its marketplace; a store or ship-country row names the store's SKU and no marketplace, so C2 places it on ALL (one store price). */
export function stageTarget(row: GridRow): Pick<StageBody, "xero_sku" | "marketplace_id"> {
  return row.channel === "amazon" ? { xero_sku: row.xero_sku, marketplace_id: row.marketplace_id } : { xero_sku: skuOf(row) };
}

/**
 * A price as a person typed it, in the form C2 accepts: digits with at most two decimals, above zero, as a string.
 * A currency sign, spaces or a comma decimal are tolerated; anything else is null and the box asks again.
 */
export function cleanPrice(raw: string): string | null {
  const text = raw.replace(/[^0-9.,]/g, "").replace(",", ".");
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!m) return null;
  const cents = (m[2] ?? "").padEnd(2, "0");
  if (/^0+$/.test(m[1] + cents)) return null;
  return `${String(Number(m[1]))}.${cents}`;
}
/** The stepper's step per currency in minor units (C1 v1.57, GATE-1083 PS-1 A): 0.50, and 5.00 where a unit is worth far less. */
export const STEP_MINOR: Record<string, number> = { SEK: 500, PLN: 500 };
const DEFAULT_STEP_MINOR = 50;
export function stepMinor(currency: string | null | undefined): number {
  return STEP_MINOR[currency ?? ""] ?? DEFAULT_STEP_MINOR;
}
/** One step in the price's own currency, in whole minor units so no float ever touches a price. Never below one step. */
export function stepPrice(value: string, direction: 1 | -1, currency?: string | null): string {
  const step = stepMinor(currency);
  const [whole, cents] = (cleanPrice(value) ?? "0.00").split(".");
  const minor = Math.max(step, Number(whole) * 100 + Number(cents) + direction * step);
  return `${Math.floor(minor / 100)}.${String(minor % 100).padStart(2, "0")}`;
}
/** A saved line the cell cannot re-save without losing a field it does not hold (a floor reason, an RRP exception or a temporary
 * price, C1 v1.57): its Save opens the drawer instead, so nothing saved with the line is dropped. */
export function needsDrawer(saved: PriceRunLine | null): boolean {
  return Boolean(saved && (saved.floor_override_reason || saved.rrp_exception_reason || saved.override_until));
}

/** The saved line for a row in its market's open run, or null. `/price-run` serves every market's runs and a store row's marketplace id is "ALL", so the market is part of the match. */
export function savedLine(runs: PriceRuns | undefined, row: GridRow, market: Market): PriceRunLine | null {
  for (const run of runs?.runs ?? []) {
    if (run.market !== market) continue;
    for (const line of run.lines) if (line.state === "saved" && lineMatches(line, row)) return line;
  }
  return null;
}
/** The row's line in its market's newest committed run: the committed price until the next grid run shows it as live. */
export function committedLine(lastRuns: PriceRun[], row: GridRow, market: Market): { line: PriceRunLine; run: PriceRun } | null {
  for (const run of lastRuns) {
    if (run.market !== market) continue;
    for (const line of run.lines) if (line.state === "committed" && lineMatches(line, row)) return { line, run };
  }
  return null;
}
/** What Commit commits: the open runs of the markets on view that hold a saved line (owner ruling A2, one market at a time, by run id). */
export function commitTarget(runs: PriceRuns | undefined, markets: Market[]): { runIds: number[]; count: number; byMarket: Array<{ market: Market; n: number }> } {
  const open = (runs?.runs ?? []).filter((run) => run.state === "open" && markets.includes(run.market));
  const withLines = open.map((run) => ({ id: run.id, market: run.market, n: run.lines.filter((line) => line.state === "saved").length })).filter((r) => r.n > 0);
  return { runIds: withLines.map((r) => r.id), count: withLines.reduce((sum, r) => sum + r.n, 0), byMarket: withLines.map((r) => ({ market: r.market, n: r.n })) };
}
/** C1 v1.38: the lines Commit sends with those run ids (C2 v1.41 `expected`), every saved line of them, as the review lists it. */
export function commitReviewed(runs: PriceRuns | undefined, markets: Market[]): ReviewedLine[] {
  const ids = new Set(commitTarget(runs, markets).runIds);
  return reviewed((runs?.runs ?? []).filter((run) => ids.has(run.id)).flatMap((run) => run.lines.filter((line) => line.state === "saved")));
}
export function lineById(runs: PriceRuns | undefined, lineId: number): PriceRunLine | null {
  for (const run of runs?.runs ?? []) for (const line of run.lines) if (line.id === lineId) return line;
  return null;
}
/** "Amazon.de", or the store name the Price Run uses. */
export function placeName(line: PriceRunLine): string {
  return line.marketplace_name ?? STORE_LABEL[line.channel_location] ?? line.channel_location;
}
/** A line named as a refusal names it: "C8 MCT Oil 500ml · Amazon.de" (C1 v1.41: the review's Remove names it the same way). */
export function lineTitle(line: PriceRunLine): string {
  return `${line.product} ${line.variant} · ${placeName(line)}`;
}

export type Refusal = { text: string; needsReason: boolean; needsException?: boolean };
/** The sentence for a refused save. A floor refusal also opens the reason field. `submitted` is the price the person sent,
 * already money-formatted, so the unchanged-price sentence never parses C2's message text. */
export function stageRefusal(error: unknown, areaName: string, currency: string | null, submitted: string, today?: string): Refusal {
  if (!(error instanceof PricingError)) return { text: COPY.unreachable, needsReason: false };
  const facts = error.facts();
  const reason = error.reason();
  const floor = money(facts.floor, facts.floor_currency);
  if (reason === "below_floor") return { text: COPY.belowFloor(floor), needsReason: true };
  if (reason === "floor_not_comparable") return { text: COPY.floorCurrency(floor, facts.floor_currency ?? "", currency ?? ""), needsReason: true };
  // C1 v1.6: a gated line below the region RRP, or in a currency with no stored rate, opens the exception fields.
  if (reason === "below_rrp") {
    const rrp = money(facts.rrp, facts.rrp_currency);
    const text = facts.fx_rate != null
      ? RRP_COPY.belowRrpConverted(submitted, money(facts.compared_price, facts.rrp_currency), humanDate(facts.fx_rate_date), Boolean(facts.fx_stale),
        facts.deviation_pct ?? null, rrp)
      : RRP_COPY.belowRrp(submitted, rrp);
    return { text, needsReason: false, needsException: true };
  }
  if (reason === "rrp_not_comparable") {
    return { text: RRP_COPY.rrpNotComparable(submitted, currency ?? "", money(facts.rrp, facts.rrp_currency)), needsReason: false, needsException: true };
  }
  // A malformed exception (until before the store's day, an over-long reason): said in words, never C2's field names.
  if (reason === "bad_exception") return { text: RRP_COPY.badException(today ? humanDate(today) : null), needsReason: false };
  // C1 v1.50: a temporary price needs both its fields and an Amazon marketplace row (C2 v1.52 `bad_override`, `override_amazon_only`).
  if (reason === "bad_override") return { text: OVERRIDE_COPY.box.bad(today ? humanDate(today) : null), needsReason: false };
  if (reason === "override_amazon_only") return { text: OVERRIDE_COPY.box.amazonOnly, needsReason: false };
  if (reason === "unchanged_price") return { text: COPY.unchanged(submitted), needsReason: false };
  if (reason === "bad_price" || reason === "bad_request") return { text: COPY.enterPrice, needsReason: false };
  if (error.status === 401 || error.status === 403) return { text: COPY.noEdit(areaName), needsReason: false };
  return { text: facts.message ?? COPY.unreachable, needsReason: false };
}
/** The sentence for a refused commit, naming every refused line by product and place. */
export function commitRefusal(error: unknown, runs: PriceRuns | undefined, areaName: string): string {
  if (!(error instanceof PricingError)) return COPY.unreachable;
  // C1 v1.38: the saved prices changed while the review was open (C2 v1.41); `runs` is the read the review showed.
  const change = reviewChange<PriceRunLine>(error);
  if (change) {
    const gone = (id: number) => { const line = lineById(runs, id); return line ? lineTitle(line) : COPY.reviewedPrice; };
    const parts = [
      ...change.added.map((l) => COPY.reviewAdded(lineTitle(l), money(l.new_price, l.currency), personName(l.staged_by))),
      ...change.resaved.map((l) => COPY.reviewResaved(lineTitle(l), money(l.new_price, l.currency), personName(l.staged_by))),
      ...change.removed.map((id) => COPY.reviewRemoved(gone(id))),
    ];
    return COPY.changedSinceReview(parts.length, parts.join("; "), "price");
  }
  const facts = error.facts();
  const reason = error.reason();
  const lines = facts.lines ?? [];
  const name = (l: RefusedLine) => {
    const line = lineById(runs, l.line_id);
    return line ? lineTitle(line) : `${l.xero_sku} · ${l.marketplace_id}`;
  };
  if (reason === "below_floor_at_commit") {
    return COPY.nothingCommittedFloor(lines.length, lines.map((l) => `${name(l)} (saved ${money(l.new_price, l.currency)}, floor now ${money(l.floor, l.floor_currency)})`).join("; "));
  }
  if (reason === "unchanged_at_commit") {
    return COPY.nothingCommittedUnchanged(lines.length, lines.map((l) => `${name(l)} (${money(l.new_price, l.currency)})`).join("; "));
  }
  if (reason === "below_rrp_at_commit") {
    const why = (l: RefusedLine) => (l.reason === "rrp_exception_ended"
      ? RRP_COPY.commitLine.rrp_exception_ended(humanDate(l.rrp_exception_until))
      : l.reason === "rrp_not_comparable"
        ? RRP_COPY.commitLine.rrp_not_comparable(money(l.new_price, l.currency), money(l.rrp, l.rrp_currency))
        : RRP_COPY.commitLine.below_rrp(money(l.new_price, l.currency), money(l.rrp, l.rrp_currency)));
    return RRP_COPY.nothingCommittedRrp(lines.length, lines.map((l) => `${name(l)} (${why(l)})`).join("; "));
  }
  if (reason === "rrp_changed_at_commit") return RRP_COPY.rrpChanged;
  if (reason === "nothing_to_commit") return COPY.nothingToCommit;
  if (error.status === 401 || error.status === 403) return COPY.noEdit(areaName);
  return facts.message ?? COPY.unreachable;
}
